// src/contexts/BookingContext.js
import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from "react";

import { useLessons } from "./LessonsContext";
import { useTeacherAvailability } from "./TeacherAvailabilityContext";
import { useCart } from "./CartContext";

const BookingContext = createContext();
export const useBooking = () => useContext(BookingContext);

/* ------------ constants ------------ */
const LESSON_MINUTES = 50;

/* ------------ tiny utils ------------ */
const pad2 = (n) => String(n).padStart(2, "0");

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const formatDateStr = (date) => {
  if (!date) return "";
  if (typeof date === "string") return date;
  try {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  } catch {
    return "";
  }
};

const hhmmToMinutes = (hhmm) => {
  const [h = 0, m = 0] = (hhmm || "00:00").split(":").map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const minutesToHHMM = (mins) => {
  const m = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
};

export const addMinutes = (hhmm, add) => minutesToHHMM(hhmmToMinutes(hhmm) + add);

// slot starts covered by a lesson range (startTime..endTime)
// NOTE: this assumes your selectable UI slots are on the hour (HH:00)
const slotStartsFromRange = (startHHMM, endHHMM) => {
  const start = hhmmToMinutes(startHHMM);
  const end = hhmmToMinutes(endHHMM);
  const out = [];
  for (let t = start; t < end; t += 60) out.push(minutesToHHMM(t).slice(0, 2) + ":00");
  return out;
};

/* Map LessonsContext status → UI labels for requests list/badges */
const mapStatusToUI = (status) => {
  switch (status) {
    case "NO_RESPONSE":
      return "pending";
    case "APPROVED":
      return "approved";
    case "NOT_APPROVED":
      return "rejected";
    case "PAID":
      return "paid";
    case "COMPLETED":
      return "completed";
    case "NO_SHOW":
      return "no_show";
    default:
      return "unknown";
  }
};

export const BookingProvider = ({ children, course: courseProp }) => {
  const fallbackCourse = useMemo(
    () => ({
      id: 0,
      title: "קורס",
      subject: "",
      price: 0, // ✅ price per 50 minutes
      teacher: { id: 0, name: "מורה", avatar: "", email: "" },
      image: "",
    }),
    []
  );

  const course = useMemo(() => courseProp ?? fallbackCourse, [courseProp, fallbackCourse]);

  const {
    lessons,
    fetchCourseLessons,
    createPrivateRequest,
    approvePrivateRequest,
    rejectPrivateRequest,
    deleteLesson,
  } = useLessons();

  const {
    availableSlots,
    currentMonth,
    setCurrentMonth,
    prevMonth,
    nextMonth,
    getDaysInMonth,
    getFirstDayOffset,
    hasAvailableSlots,
    reserveSlots,
    releaseSlots,
  } = useTeacherAvailability();

  const {requestCartRefresh }=useCart();

  const [courseLessons, setCourseLessons] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);

  // alert + spinner
  const [alert, setAlert] = useState(null);
  const [busy, setBusy] = useState(false);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    window.clearTimeout(window.__bookingAlertTimer);
    window.__bookingAlertTimer = window.setTimeout(() => setAlert(null), 3500);
  }, []);
  const clearAlert = useCallback(() => setAlert(null), []);

  // Load lessons for the specific course
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!course?.id) {
        setCourseLessons([]);
        return;
      }
      setBusy(true);
      try {
        const items = await fetchCourseLessons(course.id);
        if (!alive) return;
        setCourseLessons(Array.isArray(items) ? items : []);
      } catch {
        if (!alive) return;
        setCourseLessons([]);
        showAlert("error", "טעינת השיעורים נכשלה");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [course?.id, fetchCourseLessons, showAlert]);

  // Merge with global lessons state so status reflects latest updates
  const mergedCourseLessons = useMemo(() => {
    const byId = new Map();
    for (const l of courseLessons) byId.set(String(l.id), l);
    for (const l of lessons) {
      if (String(l.courseId) === String(course.id)) {
        byId.set(String(l.id), { ...byId.get(String(l.id)), ...l });
      }
    }
    return Array.from(byId.values());
  }, [courseLessons, lessons, course.id]);

  // Build UI list of private lesson requests for this course
  // ✅ hoursCount computed from start/end so it matches 50-min window logic
  const lessonRequests = useMemo(() => {
    return (mergedCourseLessons || [])
      .filter((l) => {
        if (String(l.courseId) !== String(course.id)) return false;
        if ((l.type || "group") !== "private") return false;

        const status = String(l.status || "").trim().toUpperCase();
        const isPurchased = !!l.purchased;

        // hide paid / purchased private requests
        if (status === "PAID") return false;
        if (isPurchased) return false;

        return true;
      })
      .map((l) => {
        const startMin = hhmmToMinutes(l?.startTime || "00:00");
        const endMin = hhmmToMinutes(l?.endTime || "00:00");
        const dur = Math.max(0, endMin - startMin);
        const slots50 = dur ? Math.ceil(dur / LESSON_MINUTES) : 0;

        return {
          id: l.id,
          date: l.date,
          startTime: l.startTime,
          endTime: l.endTime,
          price: l.price,
          hoursCount: Math.max(1, slots50 || 0),
          type: "private",

          status: l.status,
          approvedAt: l.approvedAt ?? null,
          paymentDeadlineAt: l.paymentDeadlineAt ?? null,
          paymentExpired: !!l.paymentExpired,

          purchased: !!l.purchased,
          paid: !!l.purchased,
          title: l.title || "",
        };
      })
      .sort((a, b) =>
        a.date === b.date
          ? a.startTime.localeCompare(b.startTime)
          : a.date.localeCompare(b.date)
      );
  }, [mergedCourseLessons, course.id]);

  // Date/time picking
  const handleDateSelect = useCallback((dateOrStr) => {
    const v = typeof dateOrStr === "string" ? dateOrStr : formatDateStr(dateOrStr);
    if (!v) return;
    setSelectedDate(v);
    setSelectedTimes([]);
  }, []);

  const handleTimeSelect = useCallback(
    (time) => {
      setSelectedTimes((prev) => {
        if (prev.includes(time)) return prev.filter((t) => t !== time);
        if (prev.length === 0) return [time];

        // enforce consecutive selections by hour (your UI slots are HH:00)
        const hour = parseInt(time.split(":")[0], 10);
        const sorted = [...prev].sort();
        const hours = sorted.map((t) => parseInt(t.split(":")[0], 10));
        const minH = Math.min(...hours);
        const maxH = Math.max(...hours);

        if (hour === minH - 1 || hour === maxH + 1) return [...sorted, time].sort();

        showAlert("error", "יש לבחור שעות רצופות בלבד");
        return prev;
      });
    },
    [showAlert]
  );

  // ✅ Submit private request
  // ✅ FIX #1: endTime = start + (count * 50)
  // ✅ FIX #2: price uses course.price (per 50 minutes)
  const handleSubmit = useCallback(async () => {
    if (!selectedDate || selectedTimes.length === 0) {
      showAlert("error", "יש לבחור תאריך ולפחות שעה אחת");
      return null;
    }

    const times = [...selectedTimes].sort();
    const start = times[0];

    // ✅ FIX: 2 lessons => +100 minutes => 17:40
    const endTime = addMinutes(start, times.length * LESSON_MINUTES);

    // ✅ price per 50 minutes comes from course.price
    const unitPrice = safeNumber(course?.price, 0);
    if (unitPrice <= 0) {
      showAlert("error", "לקורס אין מחיר תקין. עדכן/י מחיר בקורס לפני שליחת בקשה פרטית.");
      return null;
    }

    const price = times.length * unitPrice;

    setBusy(true);
    try {
      const created = await createPrivateRequest({
        courseId: course.id,
        title: `שיעור פרטי · ${course.title}`,
        date: selectedDate,
        startTime: start,
        endTime, // ✅ FIXED
        price,   // ✅ FIXED (even if server recalculates, it should match now)
        platform: "ZOOM",
      });

      setCourseLessons((prev) => [created, ...prev]);

      // Optimistic: remove selected slots
      reserveSlots(selectedDate, times);

      setSelectedDate(null);
      setSelectedTimes([]);
      showAlert("success", "הבקשה נשלחה בהצלחה");
      return created;
    } catch (e) {
      showAlert("error", e?.message || "יצירת בקשה נכשלה");
      return null;
    } finally {
      setBusy(false);
    }
  }, [
    selectedDate,
    selectedTimes,
    course,
    createPrivateRequest,
    reserveSlots,
    showAlert,
  ]);

  // Teacher actions
  const teacherApprove = useCallback(
    async (requestId) => {
      setBusy(true);
      try {
        const updated = await approvePrivateRequest(requestId);

        setCourseLessons((prev) =>
          prev.map((l) =>
            String(l.id) === String(requestId)
              ? { ...l, ...updated }
              : l
          )
        );

        showAlert("success", "הבקשה אושרה");
      } catch (e) {
        showAlert("error", e?.message || "אישור הבקשה נכשל");
      } finally {
        setBusy(false);
      }
    },
    [approvePrivateRequest, showAlert]
  );

  const teacherReject = useCallback(
    async (requestId) => {
      setBusy(true);
      try {
        const l = (mergedCourseLessons || []).find((x) => String(x.id) === String(requestId));
        await rejectPrivateRequest(requestId);

        setCourseLessons((prev) =>
          prev.map((x) =>
            String(x.id) === String(requestId) ? { ...x, status: "NOT_APPROVED" } : x
          )
        );

        // free slots on reject based on start/end
        if (l?.date && l?.startTime && l?.endTime) {
          // If your selectable slots are hourly, use this helper:
          const slotsToRelease = slotStartsFromRange(l.startTime, l.endTime);
          releaseSlots(l.date, slotsToRelease);
        }

        showAlert("success", "הבקשה נדחתה");
      } catch (e) {
        showAlert("error", e?.message || "דחיית הבקשה נכשלה");
      } finally {
        setBusy(false);
      }
    },
    [rejectPrivateRequest, mergedCourseLessons, releaseSlots, showAlert]
  );



  const removeRequest = useCallback(
    async (requestId) => {
      if (!requestId) return;

      setBusy(true);

      try {
        const l = mergedCourseLessons.find(
          (x) => String(x.id) === String(requestId)
        );

        if (!l) return;

        await deleteLesson(requestId); // from LessonsContext

        setCourseLessons((prev) =>
          prev.filter((x) => String(x.id) !== String(requestId))
        );

        if (l?.date && l?.startTime && l?.endTime) {
          const slotsToRelease = slotStartsFromRange(
            l.startTime,
            l.endTime
          );
          releaseSlots(l.date, slotsToRelease);
        }

        showAlert("success", "הבקשה נמחקה");

      } catch (e) {
        showAlert("error", e?.message || "מחיקת הבקשה נכשלה");
      } finally {
        setBusy(false);
      }
    },
    [
      mergedCourseLessons,
      deleteLesson,
      releaseSlots,
      showAlert,
    ]
  );


  const value = {
    course,
    lessonRequests,

    selectedDate,
    selectedTimes,
    currentMonth,
    availableSlots,
    selectedRequests,

    setSelectedDate,
    setSelectedTimes,
    setCurrentMonth,
    setSelectedRequests,

    handleDateSelect,
    handleTimeSelect,
    handleSubmit,
   
    teacherApprove,
    teacherReject,
    removeRequest,

    prevMonth,
    nextMonth,
    getDaysInMonth,
    getFirstDayOffset,
    formatDateStr,
    hasAvailableSlots,

    busy,
    alert,
    showAlert,
    clearAlert,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};